<template>
    <div class="@container/inbox flex flex-col overflow-y-auto scroll-thin md:-mt-6 md:-mr-6 md:-mb-3">
        <div class="flex flex-col gap-6 p-6 md:pl-0 md:pb-3">
            <!-- A. Header -->
            <motion.div
                :initial="fadeUpVariants.hidden"
                :animate="fadeUpVariants.visible"
                :transition="sectionTransition(0)"
                class="flex justify-center gap-2 pt-6 pb-4 items-center text-center"
            >
                <h1 class="text-3xl font-bold">Intelligent Inbox</h1>
                <vx-badge size="xs" color="violet">
                    <font-awesome-icon :icon="faSparkles"></font-awesome-icon>
                    AI
                </vx-badge>
            </motion.div>

            <!-- B. AI Daily Brief Banner -->
            <motion.div
                :initial="fadeUpVariants.hidden"
                :animate="fadeUpVariants.visible"
                :transition="sectionTransition(1)"
                class="mx-auto w-full max-w-2xl"
            >
                <vx-box-glass class="relative">
                    <vx-wallpaper :icon="faSparkles"></vx-wallpaper>
                    <div class="left-0 top-0 h-full w-full absolute bg-white/50"></div>

                    <div class="relative flex items-start gap-4 p-5">
                        <div class="flex-1 min-w-0">
                            <p class="text-center font-semibold text-slate-700 mb-1">Daily Brief</p>
                            <p class="text-sm text-slate-600">
                                You have
                                <template v-for="(item, index) in briefItems" :key="item.sectionState">
                                    <meta-badge
                                        shadow
                                        :color="item.color as MetaBadgeColor"
                                        class="ml-1 cursor-pointer transition hover:scale-105 active:scale-90"
                                        @click="focusSection(item.sectionState)"
                                        >{{ item.count }} {{ item.text }}</meta-badge
                                    ><template v-if="index < briefItems.length - 2">, </template
                                    ><template v-else-if="index === briefItems.length - 2">, and </template> </template
                                >. 3 top-priority conversations need your attention first.
                            </p>
                        </div>
                    </div>
                </vx-box-glass>
            </motion.div>

            <!-- D. Top Priority Conversations -->
            <motion.div
                :initial="fadeUpVariants.hidden"
                :animate="fadeUpVariants.visible"
                :transition="sectionTransition(3)"
            >
                <h2 class="text-base flex font-bold text-slate-800 mb-3 justify-between items-center">
                    <span>Top Priority Conversations</span>

                    <router-link :to="{ name: 'message-hub.intelligent-inbox.settings' }">
                        <vx-button color="muted-dark" size="xs">
                            <font-awesome-icon widthAuto :icon="faGear"></font-awesome-icon>
                            Settings
                        </vx-button>
                    </router-link>
                </h2>
                <motion.div
                    initial="hidden"
                    animate="visible"
                    :transition="cardStagger"
                    class="grid grid-cols-1 gap-3 @lg/inbox:grid-cols-3"
                >
                    <motion.div
                        v-for="thread in priorityThreads"
                        :key="thread.id"
                        :variants="fadeUpVariants"
                        :transition="quickTransition"
                    >
                        <vx-box class="@container p-4 flex flex-col gap-2 h-full">
                            <div class="flex justify-between min-w-0">
                                <p class="text-sm font-semibold text-slate-800 truncate">
                                    {{ thread.contactName }}
                                </p>
                                <p class="text-xs text-slate-400">{{ thread.timeAgo }}</p>
                            </div>

                            <p class="text-sm text-slate-600 line-clamp-3 flex-1">{{ thread.priorityReason }}</p>

                            <div class="rounded-lg bg-violet-50 border border-violet-100 p-3">
                                <div class="flex items-center gap-1.5 mb-1">
                                    <font-awesome-icon
                                        :icon="faSparkles"
                                        class="text-violet-500 text-xs"
                                    ></font-awesome-icon>
                                    <span class="text-xs font-semibold text-violet-600">AI Suggested Reply</span>
                                </div>
                                <p class="text-sm text-violet-900 line-clamp-2">{{ thread.suggestedReply }}</p>
                            </div>

                            <div class="flex flex-col @[300px]:flex-row @[300px]:justify-between gap-2">
                                <div class="flex items-center gap-1.5">
                                    <div
                                        class="size-2 rounded-[2px]"
                                        :class="CATEGORY_COLORS[thread.category.color]?.bg"
                                    ></div>
                                    <span class="text-xs">{{ thread.category.displayName }}</span>
                                </div>
                                <div class="flex items-center gap-2 pt-1">
                                    <vx-button color="primary" size="xs">
                                        Reply
                                        <font-awesome-icon widthAuto :icon="faReply"></font-awesome-icon>
                                    </vx-button>
                                    <vx-button color="muted" size="xs">
                                        Snooze
                                        <font-awesome-icon widthAuto :icon="faClock"></font-awesome-icon>
                                    </vx-button>
                                </div>
                            </div>
                        </vx-box>
                    </motion.div>
                </motion.div>
            </motion.div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { faSparkles, faGear, faReply, faClock } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBox, VxBoxGlass, VxButton, VxBadge, VxWallpaper } from '@voxie/frontend-components';
import { onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { motion } from 'motion-v';
import MetaBadge from '~/components/general/MetaBadge.vue';
import type { MetaBadgeColor } from '~/components/general/MetaBadge.types';
import { useIntelligentInbox, CATEGORY_COLORS } from './useIntelligentInbox';
import { useIntelligentInboxDashboard } from './useIntelligentInboxDashboard';
import { fadeUpVariants, quickTransition } from './wizard/wizardMotionVariants';
import { cardStagger, sectionTransition } from './dashboardMotionVariants';

const router = useRouter();
const { isFirstTime, focusSection } = useIntelligentInbox();

const { priorityThreads, briefItems } = useIntelligentInboxDashboard();

onMounted(() => {
    if (isFirstTime.value) {
        router.replace({ name: 'message-hub.intelligent-inbox.wizard' });
    }
});
</script>
